import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { cn } from '../../common/utils';
import { PanelHeader } from '../../components/designer/panels/PanelHeader';
import ErrorIndicator from '../../components/ErrorIndicator';
import { Container } from '../../components/layout/ContainerFlex';
import { Spacer } from '../../components/layout/Spacer';
import LoadingIndicator from '../../components/LoadingIndicator';
import FilterPresetSelector from '../../filterPreset/FilterPresetSelector';
import LayoutActiveFeatureTag from '../../layout/LayoutActiveFeatureTag';
import ActualityExtraDatasetView from '../ActualityExtraDatasetView';
import FeedDatePicker from '../components/FeedDatePicker';
import FeedLayoutViewSwitch from '../components/FeedLayoutViewSwitch';
import ViewerActivityFeed from '../layout/ViewerActivityFeed';
import {
  actualityMainViewTypeAtom,
  actualitySelectedTabIdAtom,
} from '../store/actuality.ui.state';
import ActualityAggregatedStatsFilters from './ActualityAggregatedStatsFilters';
import FeedActivityKPIRoot from './FeedActivityKPIRoot';
import ViewerJobsTimeline from './ViewerJobsTimeline';

/**
 *  Show actuality warehouse layout
 */
const ActualityMainLayoutView: React.FC = () => {
  const { t } = useTranslation('feed');
  const actualityMainViewType = useAtomValue(actualityMainViewTypeAtom);
  const selectedTabId = useAtomValue(actualitySelectedTabIdAtom);

  function getContent() {
    switch (selectedTabId) {
      case 'tab-feed-kpi':
        return <FeedActivityKPIRoot />;
      default:
        return <ViewerActivityFeed />;
    }
  }
  const showLayout = actualityMainViewType !== 'timeline';
  const showTimeline = actualityMainViewType !== 'layout';
  return (
    <Container
      col
      componentName="ActualityMainLayoutView"
      className={cn('border-app-panel-dark', 'border-x-px')}
    >
      <PanelHeader className="z-[9999]" title={''}>
        <Suspense fallback={<LoadingIndicator inline transparent />}>
          <FilterPresetSelector />
          <FeedDatePicker selectorPosition="start" />
          <Spacer flexspace />
          <ActualityAggregatedStatsFilters />
          <LayoutActiveFeatureTag />
          <FeedLayoutViewSwitch />
        </Suspense>
      </PanelHeader>
      <ErrorBoundary
        fallbackRender={({ error, resetErrorBoundary }) => (
          <ErrorIndicator
            selfCenter
            message={error.message}
            hasReloadButton
            exception={error}
          />
        )}
      >
        <Suspense
          fallback={
            <LoadingIndicator
              className={cn(
                'bg-map-area-striped',
                'border-app-background border-8',
              )}
              selfCenter
              message={t`Loading Data`}
            />
          }
        >
          {showLayout && (
            <Container col overflow flex1>
              {getContent()}
            </Container>
          )}
          {showTimeline && <ViewerJobsTimeline />}
          <ActualityExtraDatasetView />
        </Suspense>
      </ErrorBoundary>
    </Container>
  );
};

export default ActualityMainLayoutView;
